import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { PaymentFormProps } from './types';
import messages from './messages';
import AffirmForm from '../../AffirmForm';
import AffirmAsLowAs from '../../AffirmAsLowAs';
import {
  calculateTotalGiftCards,
  isSameAddressWithName,
  scrollToElement,
} from '../../../utils';
import { getAttributeValue } from '../../../utils/variants';
import RadioButton from '../../common/RadioButton';

import { paymentMethods } from '../../../slices/payments';
import styles from './PaymentForm.module.scss';
import useApplePaySession from '../../../hooks/useApplePaySession';
import CloverForm from '../CloverForm/CloverForm';
import ChargeAfterSdk from '../../ChargeAfterSdk';
import ChargeAfterWidget from '../../ChargeAfterWidget';
import { UseApplePaySessionResult } from '../../ApplePayButton/types';
import SecondaryButton from '../../common/SecondaryButton';
import TurnstileWidget from '../../TurnstileWidget/TurnstileWidget';
import ChargeAfterButton from '../../ChargeAfterButton';
import acimaLogo from '../../../assets/images/svg/acima-logo.svg';
import truemedIcon from '../../../assets/images/svg/truemed-icon.svg';
import AvailablePaymentMethods from '../../AvailablePaymentMethods';

import {
  AFFIRM_MAXIMUM_CART_VALUE_CENTAMOUNT,
  CHARGE_AFTER_WIDGET_TAGS,
  CHARGE_AFTER_MINIMUM_CART_VALUE_CENTAMOUNT,
  AFFIRM_MINIMUM_VALUE_CENTAMOUNT,
  CHARGE_AFTER_MINIMUM_PRODUCT_DISCOUNTED_PRICE_CENTAMOUNT,
  AFFIRM_WIDGET_TEST_VARIANTS,
  STANDARD_SHIPPING_KEY,
  STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
  SHOW_AFFIRM_BLOCK_ENUM,
  AFFIRM_PRICE_LOWER_THRESHOLD,
  AFFIRM_PRICE_UPPER_THRESHOLD,
  AFFIRM_PRICE_CONDITIONAL_THRESHOLD
} from '../../../utils/common/constants';
import { getCookie } from '../../../utils/common';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import PrimaryButton from '../../common/PrimaryButton';
import AffirmButton from '../../AffirmButton';
import ApplePayButton from '../../ApplePayButton/ApplePayButton';
import PaypalButton from '../../PaypalButton';
import AmazonPayButton from '../../AmazonPayButton';
import SelectBillingAddress from '../../SteppedSelectBillingAddress';
import { CartInStore } from '../../../slices/cart/types';
import TruemedButton from '../TruemedButton';

import {
  PAYPALIDS,
  IS_NORMAL_AMAZON_CHECKOUT_KEY,
} from '../../CompleteOrderButton';
import { PaymentsInStore } from '../../../slices/payments/types';
import ShopPayButton from '../../ShopPayButton';
import AcimaButton from '../../AcimaButton/AcimaButton';
import { SetShippingMethodsEntry } from '../../../slices/shippingMethods/types';
import StripeForm from '../StripeForm';
import { ACIMA_SDK_URL } from '../../AcimaButton/constants';
import { CUSTOM_ACIMA_ID, CUSTOM_AFFIRM_ID, CUSTOM_CHARGEAFTER_ID, CUSTOM_SHOP_PAY_ID, CUSTOM_TRUEMED_ID } from './constants';

const IS_LEASABLE_ATTRIBUTE_NAME = 'leasable';
const paypalButtonsIdsToRender = [
  PAYPALIDS.main,
  PAYPALIDS.stepped,
  PAYPALIDS.steppedExpress,
];

const paypalButtonsThatReplacesDataWithTheInfoFromPaypal = [
  PAYPALIDS.steppedExpress,
];

const PaymentForm = ({
  selectedPaymentMethod,
  onChangeSelectedPaymentMethod,
  paymentMethodsDisabled,
  handleSavePaymentForm,
  shippingAddress,
  billingAddress,
  isTouched,
  handleOnValidateChange,
  isCompleted,
  cancelPaymentMethodHandler,
  truemedButtonRef,
  acimaButtonRef,
  shopPayButtonRef,
  chargeAfterButtonRef,
  affirmButtonRef,
}: PaymentFormProps) => {
  const [selectedStripePaymentMethodId, setSelectedStripePaymentMethodId] =
    useState<string>();
  const { formatMessage } = useIntl();
  const { lineItems, giftCards, isDraft } = useSelector(
    (state: { cart: CartInStore }) => state.cart,
  );
  const { affirmHide, affirmShow, affirmUpdate } = SHOW_AFFIRM_BLOCK_ENUM;

  const stripePaymentMethodsLabels = {
    [paymentMethods.CREDIT_CARD]: formatMessage(
      messages.creditCardRowPaymentLabel,
    ),
    [paymentMethods.TRUEMED]: formatMessage(messages.truemedPaymentLabel),
    [paymentMethods.ACIMA]: formatMessage(messages.acimaPaymentLabel),
    [paymentMethods.AFFIRM]: formatMessage(messages.affirmLogoAlt),
    [paymentMethods.CHARGE_AFTER]: formatMessage(
      messages.chargeAfterPaymentLabel,
    ),
    [paymentMethods.SHOP_PAY]: formatMessage(messages.shopPayPaymentLabel),
    amazon_pay: formatMessage(messages.amazonPayPaymentLabel),
    affirm: formatMessage(messages.affirmPaymentLabel),
    link: formatMessage(messages.linkPaymentLabel),
    klarna: formatMessage(messages.klarnaPaymentLabel),
    afterpay: formatMessage(messages.afterpayPaymentLabel),
    card: formatMessage(messages.creditCardPaymentLabel),
    default: formatMessage(messages.defaultPaymentLabel),
  };

  const stripeCTAMessagesPerPaymentMethodId = {
    [process.env.STRIPE_CUSTOM_AFFIRM_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodContinueWith,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.AFFIRM] },
    ),
    [process.env.STRIPE_CUSTOM_ACIMA_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodContinueWith,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.ACIMA] },
    ),
    [process.env.STRIPE_CUSTOM_TRUEMED_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodContinueWith,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.TRUEMED] },
    ),
    [process.env.STRIPE_CUSTOM_CHARGEAFTER_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodContinueWith,
      {
        paymentMethod: stripePaymentMethodsLabels[paymentMethods.CHARGE_AFTER],
      },
    ),
    [process.env.STRIPE_CUSTOM_SHOP_PAY_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodContinueWith,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.SHOP_PAY] },
    ),
    amazon_pay: formatMessage(messages.customPaymentMethodContinueWith, {
      paymentMethod: stripePaymentMethodsLabels.amazon_pay,
    }),
    affirm: formatMessage(messages.customPaymentMethodContinueWith, {
      paymentMethod: stripePaymentMethodsLabels.affirm,
    }),
    klarna: formatMessage(messages.customPaymentMethodContinueWith, {
      paymentMethod: stripePaymentMethodsLabels.klarna,
    }),
    afterpay_clearpay: formatMessage(messages.customPaymentMethodContinueWith, {
      paymentMethod: stripePaymentMethodsLabels.afterpay,
    }),
    link: formatMessage(messages.customPaymentMethodContinueWith, {
      paymentMethod: stripePaymentMethodsLabels.link,
    }),
    card: formatMessage(messages.customPaymentMethodContinueWith, {
      paymentMethod: stripePaymentMethodsLabels.card,
    }),
    default: formatMessage(messages.customPaymentMethodContinueWith, {
      paymentMethod: stripePaymentMethodsLabels.default,
    }),
    billing_address: formatMessage(messages.billingAddressCTAText),
  };

  const stripeCTAEditMessagesPerPaymentMethodId = {
    [process.env.STRIPE_CUSTOM_AFFIRM_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodChangeTo,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.AFFIRM] },
    ),
    [process.env.STRIPE_CUSTOM_ACIMA_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodChangeTo,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.ACIMA] },
    ),
    [process.env.STRIPE_CUSTOM_TRUEMED_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodChangeTo,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.TRUEMED] },
    ),
    [process.env.STRIPE_CUSTOM_CHARGEAFTER_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodChangeTo,
      {
        paymentMethod: stripePaymentMethodsLabels[paymentMethods.CHARGE_AFTER],
      },
    ),
    [process.env.STRIPE_CUSTOM_SHOP_PAY_ID ?? 'default']: formatMessage(
      messages.customPaymentMethodChangeTo,
      { paymentMethod: stripePaymentMethodsLabels[paymentMethods.SHOP_PAY] },
    ),
    amazon_pay: formatMessage(messages.customPaymentMethodChangeTo, {
      paymentMethod: stripePaymentMethodsLabels.amazon_pay,
    }),
    affirm: formatMessage(messages.customPaymentMethodChangeTo, {
      paymentMethod: stripePaymentMethodsLabels.affirm,
    }),
    link: formatMessage(messages.customPaymentMethodChangeTo, {
      paymentMethod: stripePaymentMethodsLabels.link,
    }),
    card: formatMessage(messages.defaultEditLabel),
    default: formatMessage(messages.defaultEditLabel),
    billing_address: formatMessage(messages.billingAddressCTAText),
  };

  const affirmPromoABTest = window?.purTogglesEnabled('affirm_promo') ?? false;

  const { selectedShippingMethods } = useSelector(
    (state: {
      shippingMethods: { selectedShippingMethods: SetShippingMethodsEntry[] };
    }) => state.shippingMethods,
  );

  const shippingMethodsSelected = Array.from(
    new Set(
      selectedShippingMethods?.flatMap((shippingMethod) =>
        shippingMethod.groups?.flatMap((group) => group.key),
      ) ?? [],
    ),
  );

  const onlyStandardShippingSelected =
    shippingMethodsSelected.length === 1 &&
    shippingMethodsSelected[0] === STANDARD_SHIPPING_KEY;

  /**
   * This toggle will show the Stripe payment element in the credit card area,
   * and show all the unsupported payment buttons so that Stripe's payment
   * element can trigger clicks on them.
   */
  const isStripeRegularCheckoutEnabled = window?.purTogglesEnabled?.(
    STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
  );

  const isCloverRegularCheckoutEnabled = !isStripeRegularCheckoutEnabled;

  const isStripeAffirm = window?.purTogglesEnabled?.('stripe_affirm');

  const [isPaymentButtonDisabled, setIsPaymentButtonDisabled] = useState(false);
  const [
    isStripePaymentMethodSelectedCompletely,
    setIsStripePaymentMethodSelectedCompletely,
  ] = useState(true);

  const handleIsPaymentButtonDisabled = useCallback((isDisabled: boolean) => {
    setIsPaymentButtonDisabled(isDisabled);
  }, []);

  const handleOnChangeSelectedPaymentMethod = useCallback(
    (newPaymentMethodData?: StripePaymentElementChangeEvent) => {
      const newSelectedPaymentMethodId = newPaymentMethodData?.value?.type;
      const selectionIsComplete = newPaymentMethodData?.complete;
      if (newSelectedPaymentMethodId) {
        setSelectedStripePaymentMethodId(newSelectedPaymentMethodId);
      }

      if (typeof selectionIsComplete === 'boolean') {
        setIsStripePaymentMethodSelectedCompletely(selectionIsComplete);
      }
    },
    [],
  );

  useEffect(() => {
    setIsPaymentButtonDisabled(isCompleted);
  }, [isCompleted]);

  const { isApplePayAvailable } =
    useApplePaySession() as UseApplePaySessionResult;

  const containsNonLeasableItems = useMemo(
    () =>
      lineItems.some(
        ({ variant }) =>
          !getAttributeValue(variant.attributes, IS_LEASABLE_ATTRIBUTE_NAME),
      ),
    [lineItems],
  );

  const totalFromStore = useSelector((state: { cart: CartInStore }) => {
    if (state.cart.taxedPrice) {
      return state.cart.taxedPrice.totalGross;
    }
    return state.cart.totalPrice;
  });

  const { status: paymentStatus } = useSelector<{
    payments: PaymentsInStore;
  }>((state) => state.payments) as {
    status: string;
  };

  const total = useMemo(() => ({
    centAmount: Math.max(
      0,
      totalFromStore.centAmount - calculateTotalGiftCards({ giftCards }),
    ),
    currencyCode: totalFromStore.currencyCode,
    fractionDigits: totalFromStore.fractionDigits,
  }), [totalFromStore, giftCards]);

    const showAffirm = useMemo(() => {
    if (!total || typeof total.centAmount !== 'number') {
      return affirmHide;
    }
    if (
      total.centAmount < AFFIRM_PRICE_LOWER_THRESHOLD ||
      total.centAmount > AFFIRM_PRICE_UPPER_THRESHOLD
    ) {
      return affirmHide;
    }
    if (
      total.centAmount >= AFFIRM_PRICE_CONDITIONAL_THRESHOLD &&
      total.centAmount < AFFIRM_PRICE_UPPER_THRESHOLD
    ) {
      return affirmUpdate;
    }
    return affirmShow;
  }, [affirmHide, affirmShow, affirmUpdate, total]);

  const { featureToggle } = useDrupalSettings() as { featureToggle: string[] };

  const financingWidgetsAndPaymentMethodsABTestEnabled =
    featureToggle?.includes('chargeafter');

  const totalIsHigherThanMinimumPDPValueForChargeAfter =
    total.centAmount >=
    CHARGE_AFTER_MINIMUM_PRODUCT_DISCOUNTED_PRICE_CENTAMOUNT;

  const enableChargeAfterRegularCheckout = window?.purTogglesEnabled(
    'charge_after_regular_checkout',
  );
  const showChargeAfterPaymentMethod =
    isDraft ||
    (enableChargeAfterRegularCheckout &&
      total.centAmount >= CHARGE_AFTER_MINIMUM_CART_VALUE_CENTAMOUNT);

  const showTrueMedPaymentMethod =
    window?.purTogglesEnabled('truemed_payment') ?? false;
  const showAcimaPaymentMethod =
    (window?.purTogglesEnabled('acima_payment') && !isDraft) ?? false;

  const isABTestingFinancingBannerVariant = getCookie(
    process.env.FINANCING_BANNER_AB_TEST_COOKIE_NAME,
  )?.value;

  const totalIsHigherThanMinimumForAffirm =
    total?.centAmount > AFFIRM_MINIMUM_VALUE_CENTAMOUNT;

  const showAffirmPaymentMethod =
    ((!financingWidgetsAndPaymentMethodsABTestEnabled &&
      totalIsHigherThanMinimumForAffirm) ||
      (financingWidgetsAndPaymentMethodsABTestEnabled &&
        !totalIsHigherThanMinimumPDPValueForChargeAfter)) &&
    (!isABTestingFinancingBannerVariant ||
      total.centAmount <= AFFIRM_MAXIMUM_CART_VALUE_CENTAMOUNT);

  const isPaymentLoading = paymentStatus === 'loading';
  const isPaymentVerifying = paymentStatus === 'verifying';

  const showPaypalButton = window?.purTogglesEnabled('paypal_buttons') ?? false;

  const showShopPayRegularCheckout = window?.purTogglesEnabled(
    'shop_pay_regular_checkout',
  );

  const [isBillingAddressSame, setIsBillingAddressSame] = useState(true);
  useEffect(() => {
    setIsBillingAddressSame(
      !billingAddress || isSameAddressWithName(billingAddress, shippingAddress),
    );
  }, [billingAddress, shippingAddress]);

  const getCTATextForStripe = () => {
    if (!isBillingAddressSame) {
      return stripeCTAEditMessagesPerPaymentMethodId.billing_address;
    }

    if (isCompleted) {
      return (
        stripeCTAEditMessagesPerPaymentMethodId[
          selectedStripePaymentMethodId ?? 'default'
        ] ?? stripeCTAEditMessagesPerPaymentMethodId.default
      );
    }
    return (
      stripeCTAMessagesPerPaymentMethodId[
        selectedStripePaymentMethodId ?? 'default'
      ] ?? stripeCTAMessagesPerPaymentMethodId.default
    );
  };

  const ctaText = getCTATextForStripe();

  const handleOnClickAmazonButton = () =>
    localStorage.setItem(IS_NORMAL_AMAZON_CHECKOUT_KEY, 'true');

  useEffect(() => {
    const acimaScript = document.getElementById('acima-script');
    if (showAcimaPaymentMethod && !window.Acima && !acimaScript) {
      const s = document.createElement('script');
      s.id = 'acima-script';
      s.type = 'text/javascript';
      s.async = true;
      s.src = ACIMA_SDK_URL;
      document.getElementsByTagName('body')[0].appendChild(s);
    }
  }, [showAcimaPaymentMethod]);

  const getSelectedStripeMethod = (paymentId?: string) => {
    switch (paymentId) {
      case CUSTOM_TRUEMED_ID:
        return paymentMethods.TRUEMED;
      case CUSTOM_ACIMA_ID:
        return paymentMethods.ACIMA;
      case CUSTOM_AFFIRM_ID:
        return paymentMethods.AFFIRM;
      case CUSTOM_CHARGEAFTER_ID:
        return paymentMethods.CHARGE_AFTER;
      case CUSTOM_SHOP_PAY_ID:
        return paymentMethods.SHOP_PAY;
      default:
        return paymentId || paymentMethods.STRIPE;
    }
  };

  const resetPaymentMethodToDefault = () => {
    onChangeSelectedPaymentMethod(paymentMethods.CREDIT_CARD);
  };

  const markerRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setSticky] = useState(true);
  useEffect(() => {
    const handleScroll = () => {
      if (!markerRef.current) return;

      const markerTop = markerRef.current.getBoundingClientRect().top;
      const stickyTop = window.scrollY;

      // Show sticky div only if marker is within viewport (not yet passed)
      // need to check if the scroll is already passed the marker top in the case that the markerTop ref is already visible on the page
      const adaButtonFrame = document.getElementById('ada-button-frame');
      if (markerTop >= stickyTop) {
        setSticky(true);
        if (adaButtonFrame) {
          adaButtonFrame.style.height = '0px';
        }
      } else {
        setSticky(false);
        if (adaButtonFrame) {
          adaButtonFrame.style.height = 'auto';
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={styles['payments-container']}>
      {paymentMethodsDisabled && (
        <div className={styles['overlay-disable-payment-methods']} />
      )}
      <div className={styles['payment-info']}>
        <div className={styles['payment-form-cc']}>
          <div
            data-testid="payment-form-credit-card"
            className={classnames(styles['payment-options-row'], {
              [styles.selected]:
                !isStripeRegularCheckoutEnabled &&
                selectedPaymentMethod === paymentMethods.CREDIT_CARD,
              [styles['not-selected']]:
                !isStripeRegularCheckoutEnabled &&
                selectedPaymentMethod !== paymentMethods.CREDIT_CARD,
              [styles['hide-overflow']]: isCloverRegularCheckoutEnabled,
            })}
            onClick={() => {
              onChangeSelectedPaymentMethod(paymentMethods.CREDIT_CARD);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onChangeSelectedPaymentMethod(paymentMethods.CREDIT_CARD);
              }
            }}
          >
            {!isStripeRegularCheckoutEnabled && (
              <div className={styles['payment-info-credit-card-title']}>
                <RadioButton
                  name="paymentMethodOption"
                  id={`payment-${paymentMethods.CREDIT_CARD}`}
                  checked={selectedPaymentMethod === paymentMethods.CREDIT_CARD}
                  value={paymentMethods.CREDIT_CARD}
                  onChange={() => {
                    onChangeSelectedPaymentMethod(paymentMethods.CREDIT_CARD);
                  }}
                  theme="unselected-circle"
                  dataTestId="credit-card-radioButton"
                  label={
                    <div className={styles['credit-card-row--title']}>
                      <div className={styles.title}>
                        {formatMessage(messages.creditCardRowTitle)}
                      </div>
                      <div className={styles['card-name-info']}>
                        <AvailablePaymentMethods total={total?.centAmount} />
                      </div>
                    </div>
                  }
                />
              </div>
            )}
            {isCloverRegularCheckoutEnabled &&
              selectedPaymentMethod === paymentMethods.CREDIT_CARD &&
              !paymentMethodsDisabled && (
                <CloverForm
                  handleIsPaymentButtonDisabled={handleIsPaymentButtonDisabled}
                />
              )}
            {isStripeRegularCheckoutEnabled &&
              [
                paymentMethods.CREDIT_CARD,
                paymentMethods.TRUEMED,
                paymentMethods.ACIMA,
                paymentMethods.CHARGE_AFTER,
                paymentMethods.AMAZON_PAY,
                paymentMethods.SHOP_PAY,
              ].includes(selectedPaymentMethod) &&
              !paymentMethodsDisabled && (
                  <StripeForm
                    shippingAddress={shippingAddress}
                    billingAddress={billingAddress ?? shippingAddress}
                    handleIsPaymentButtonDisabled={
                      handleIsPaymentButtonDisabled
                    }
                    onChangeSelectedPaymentMethod={
                      handleOnChangeSelectedPaymentMethod
                    }
                    isTouched={isTouched}
                    onChangeBilling={handleOnValidateChange}
                  />
              )}

            {!isStripeRegularCheckoutEnabled && (
              <SelectBillingAddress
                onValidateChange={handleOnValidateChange}
                isTouched={isTouched}
                isActive={
                  !!shippingAddress &&
                  selectedPaymentMethod === paymentMethods.CREDIT_CARD
                }
              />
            )}
            <div ref={markerRef} style={{ height: 0 }} />
            <div
              aria-hidden
              className={classnames(styles['checkout-spacer'], {
                [styles['spacer--open']]: isSticky,
              })}
            />
            {(isStripeRegularCheckoutEnabled ||
              selectedPaymentMethod === paymentMethods.CREDIT_CARD) && (
              <div className={styles['buttons-container']}>
                {isCloverRegularCheckoutEnabled && (
                  <div>
                    <TurnstileWidget />
                  </div>
                )}
                <div
                  className={classnames(
                    styles['buttons-container-credit-card'],
                    {
                      [styles['buttons-container-credit-card-completed']]:
                        isCompleted,
                    },
                  )}
                >
                  {isCompleted && (
                    <SecondaryButton
                      onClick={cancelPaymentMethodHandler}
                      title={formatMessage(messages.cancelButtonLabel)}
                    />
                  )}
                  <div className={classnames(styles['button-container'], {
                    [styles.sticky]: isSticky,
                    [styles['not-sticky']]: !isSticky,
                  })}>
                    <PrimaryButton
                      onClick={handleSavePaymentForm as (data: unknown) => void}
                      title={
                        isStripeRegularCheckoutEnabled
                          ? ctaText
                          : formatMessage(messages.confirmButton)
                      }
                      id="save-payment-button"
                      buttonId={
                        isStripeRegularCheckoutEnabled
                          ? `${getSelectedStripeMethod(selectedStripePaymentMethodId)}-submit-btn`
                          : `${selectedPaymentMethod}-submit-btn`
                      }
                      isLoading={isPaymentLoading || isPaymentVerifying}
                      isDisabled={
                        isPaymentButtonDisabled ||
                        !isStripePaymentMethodSelectedCompletely
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isStripeRegularCheckoutEnabled && isApplePayAvailable && (
        <div
          className={classnames(
            styles['payment-options-row'],
            styles['payment-info-row-apple-pay'],
            {
              [styles.selected]:
                selectedPaymentMethod === paymentMethods.APPLE_PAY,
              [styles['not-selected']]:
                selectedPaymentMethod !== paymentMethods.APPLE_PAY,
              [styles.hidden]: isStripeRegularCheckoutEnabled,
            },
          )}
          onClick={() => {
            onChangeSelectedPaymentMethod(paymentMethods.APPLE_PAY);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onChangeSelectedPaymentMethod(paymentMethods.APPLE_PAY);
            }
          }}
        >
          <div className={styles['payment-info-row-title']}>
            <div className={styles['payment-info-row-button']}>
              <RadioButton
                name="paymentMethodOption"
                id={`payment-${paymentMethods.APPLE_PAY}`}
                checked={selectedPaymentMethod === paymentMethods.APPLE_PAY}
                value={paymentMethods.APPLE_PAY}
                onChange={() => {
                  onChangeSelectedPaymentMethod(paymentMethods.APPLE_PAY);
                }}
                theme="unselected-circle"
                dataTestId="applePay-radioButton"
                label={
                  <div className={styles['payment-info-row-logo']}>
                    <img
                      alt={formatMessage(messages.applePayLogoAlt)}
                      src={`${process.env.ASSETS_URL}/images/svg/apple-pay-logo.svg`}
                    />
                  </div>
                }
              />
            </div>
          </div>
          {selectedPaymentMethod === paymentMethods.APPLE_PAY && (
            <div className={styles['button-container']}>
              <ApplePayButton onCompleteCheckout={handleSavePaymentForm} />
            </div>
          )}
        </div>
      )}
      {(isStripeRegularCheckoutEnabled ||
        (showShopPayRegularCheckout && onlyStandardShippingSelected)) && (
        <div
          className={classnames(
            styles['payment-options-row'],
            styles['payment-info-row-apple-pay'],
            {
              [styles.hidden]: isStripeRegularCheckoutEnabled,
              [styles.selected]:
                selectedPaymentMethod === paymentMethods.SHOP_PAY,
              [styles['not-selected']]:
                selectedPaymentMethod !== paymentMethods.SHOP_PAY,
            },
          )}
        >
          <div className={styles['payment-info-row-title']}>
            <div className={styles['payment-info-row-button']}>
              <RadioButton
                name="paymentMethodOption"
                id={`payment-${paymentMethods.SHOP_PAY}`}
                checked={selectedPaymentMethod === paymentMethods.SHOP_PAY}
                value={paymentMethods.SHOP_PAY}
                onChange={() => {
                  onChangeSelectedPaymentMethod(paymentMethods.SHOP_PAY);
                }}
                theme="unselected-circle"
                dataTestId="shopPay-radioButton"
                label={
                  <div className={styles['payment-info-row-logo']}>
                    <img
                      alt={formatMessage(messages.shopPayLogoAlt)}
                      src={`${process.env.ASSETS_URL}/images/svg/shop-pay-logo.svg`}
                    />
                  </div>
                }
              />
            </div>
          </div>
          {(selectedPaymentMethod === paymentMethods.SHOP_PAY ||
            isStripeRegularCheckoutEnabled) && (
            <div className={classnames(styles['button-container'])}>
              <ShopPayButton
                id="shop-pay-button-regular-checkout-container"
                ref={shopPayButtonRef}
              />
            </div>
          )}
        </div>
      )}

      {!isStripeRegularCheckoutEnabled && showPaypalButton && (
        <div
          className={classnames(styles['payment-options-row'], {
            [styles['payment-info-row-paypal']]: showChargeAfterPaymentMethod,
            [styles.selected]: selectedPaymentMethod === paymentMethods.PAYPAL,
            [styles['not-selected']]:
              selectedPaymentMethod !== paymentMethods.PAYPAL,
            [styles.hidden]: isStripeRegularCheckoutEnabled,
          })}
          onClick={() => {
            onChangeSelectedPaymentMethod(paymentMethods.PAYPAL);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onChangeSelectedPaymentMethod(paymentMethods.PAYPAL);
            }
          }}
        >
          <div className={styles['payment-info-row-title']}>
            <div className={styles['payment-info-row-button']}>
              <RadioButton
                name="paymentMethodOption"
                id={`payment-${paymentMethods.PAYPAL}`}
                checked={selectedPaymentMethod === paymentMethods.PAYPAL}
                value={paymentMethods.PAYPAL}
                onChange={() => {
                  onChangeSelectedPaymentMethod(paymentMethods.PAYPAL);
                }}
                theme="unselected-circle"
                dataTestId="payPal-radioButton"
                label={
                  <div
                    className={classnames(
                      styles['payment-info-row-logo'],
                      styles['payment-info-row-logo-paypal'],
                    )}
                  >
                    <img
                      alt={formatMessage(messages.payPalLogoAlt)}
                      src={`${process.env.ASSETS_URL}/images/svg/paypal-logo.svg`}
                    />
                  </div>
                }
              />
            </div>
          </div>
          {selectedPaymentMethod === paymentMethods.PAYPAL && (
            <div className={styles['button-container']}>
              <PaypalButton
                id={PAYPALIDS.stepped}
                isRendererButton
                buttonsToRenderIds={paypalButtonsIdsToRender}
                buttonsIdsThatReplacesDataWithTheInfoFromPaypal={
                  paypalButtonsThatReplacesDataWithTheInfoFromPaypal
                }
                onApprove={handleSavePaymentForm}
              />
            </div>
          )}
        </div>
      )}
      {!isStripeRegularCheckoutEnabled && (
        <div
          className={classnames(
            styles['payment-options-row'],
            styles['payment-info-row-amazon-pay'],
            {
              [styles.selected]:
                selectedPaymentMethod === paymentMethods.AMAZON_PAY,
              [styles['not-selected']]:
                selectedPaymentMethod !== paymentMethods.AMAZON_PAY,
              [styles.hidden]: isStripeRegularCheckoutEnabled,
            },
          )}
          onClick={() => {
            onChangeSelectedPaymentMethod(paymentMethods.AMAZON_PAY);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onChangeSelectedPaymentMethod(paymentMethods.AMAZON_PAY);
            }
          }}
        >
          <div className={styles['payment-info-row-title']}>
            <div className={styles['payment-info-row-button']}>
              <RadioButton
                name="paymentMethodOption"
                id={`payment-${paymentMethods.AMAZON_PAY}`}
                checked={selectedPaymentMethod === paymentMethods.AMAZON_PAY}
                value={paymentMethods.AMAZON_PAY}
                onChange={() => {
                  onChangeSelectedPaymentMethod(paymentMethods.AMAZON_PAY);
                }}
                theme="unselected-circle"
                dataTestId="amazonPay-radioButton"
                label={
                  <div
                    className={classnames(
                      styles['payment-info-row-logo'],
                      styles['payment-info-row-logo-amazon-pay'],
                    )}
                  >
                    <img
                      alt={formatMessage(messages.amazonPayLogoAlt)}
                      src={`${process.env.ASSETS_URL}/images/svg/amazonpay-logo.svg`}
                    />
                  </div>
                }
              />
            </div>
          </div>
          {selectedPaymentMethod === paymentMethods.AMAZON_PAY && (
            <div className={styles['button-container']}>
              <AmazonPayButton
                id="PaymentBlockAmzBtn"
                onClick={handleOnClickAmazonButton}
                isSteppedCheckout
              />
            </div>
          )}
        </div>
      )}
      {!!showAffirm && !isStripeAffirm && (
        <div
          className={classnames(styles['payment-options-row'], {
            [styles['payment-info-row-affirm']]: showChargeAfterPaymentMethod,
            [styles.hidden]: !showAffirmPaymentMethod,
            [styles.selected]: selectedPaymentMethod === paymentMethods.AFFIRM,
            [styles['not-selected']]:
              selectedPaymentMethod !== paymentMethods.AFFIRM,
            [styles.hidden]: isStripeRegularCheckoutEnabled,
          })}
          onClick={() => {
            onChangeSelectedPaymentMethod(paymentMethods.AFFIRM);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onChangeSelectedPaymentMethod(paymentMethods.AFFIRM);
            }
          }}
        >
          <div className={styles['payment-info-row-title']}>
            <div
              className={styles['payment-info-row-button-and-legend-affirm']}
            >
              <div className={styles['payment-info-row-button']}>
                <RadioButton
                  name="paymentMethodOption"
                  id={`payment-${paymentMethods.AFFIRM}`}
                  checked={selectedPaymentMethod === paymentMethods.AFFIRM}
                  value={paymentMethods.AFFIRM}
                  onChange={() => {
                    onChangeSelectedPaymentMethod(paymentMethods.AFFIRM);
                  }}
                  disabled={containsNonLeasableItems}
                  theme="unselected-circle"
                  dataTestId="affirm-radioButton"
                  label={
                    <div
                      className={classnames(
                        styles['payment-info-row-logo'],
                        styles['payment-info-row-logo-affirm'],
                      )}
                    >
                      <img
                        alt={formatMessage(messages.affirmLogoAlt)}
                        src={`${process.env.ASSETS_URL}/images/svg/affirm-logo.svg`}
                        className={styles['affirm-img']}
                      />
                    </div>
                  }
                />
              </div>

              {selectedPaymentMethod !== paymentMethods.AFFIRM &&
                containsNonLeasableItems && (
                  <div className={styles['affirm-info']}>
                    {formatMessage(messages.affirmNotAvailable)}
                  </div>
                )}
              {selectedPaymentMethod !== paymentMethods.AFFIRM && (
                <>
                  {!containsNonLeasableItems && affirmPromoABTest && (
                    <div className={styles['affirm-info']}>
                      {formatMessage(messages.affirmLegendWithPrice, {
                        price: (
                          <AffirmAsLowAs
                            total={total?.centAmount}
                            skus={AFFIRM_WIDGET_TEST_VARIANTS.test.variant}
                            showAffirm={showAffirm}
                          />
                        ),
                      })}
                    </div>
                  )}

                  {!containsNonLeasableItems && !affirmPromoABTest && (
                    <div className={styles['affirm-info']}>
                      {formatMessage(messages.affirmLegend)}
                    </div>
                  )}
                </>
              )}
              {((isStripeRegularCheckoutEnabled && !isStripeAffirm) ||
                selectedPaymentMethod === paymentMethods.AFFIRM) && (
                <>
                  {affirmPromoABTest ? (
                    <div
                      className={
                        styles['pricing-subtotal-finance-affirm-promo']
                      }
                    >
                      {formatMessage(messages.affirmLegendWithPrice, {
                        price: (
                          <AffirmAsLowAs
                            total={total?.centAmount}
                            skus={AFFIRM_WIDGET_TEST_VARIANTS.test.variant}
                            showAffirm={showAffirm}
                          />
                        ),
                      })}
                    </div>
                  ) : (
                    <div className={styles['affirm-widget-container']}>
                      <div>{formatMessage(messages.affirmAsLowAsLegend)}</div>
                      <div>
                        <AffirmAsLowAs
                          total={total?.centAmount}
                            skus={AFFIRM_WIDGET_TEST_VARIANTS.control.variant}
                            showAffirm={showAffirm}
                        />
                      </div>
                    </div>
                  )}
                  <div
                    ref={affirmButtonRef}
                    className={styles['affirm-button-container']}
                  >
                    <AffirmButton
                      onSuccess={handleSavePaymentForm}
                      isLoading={false}
                      onFailure={onChangeSelectedPaymentMethod}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <AffirmForm />
        </div>
      )}
      {showAcimaPaymentMethod && (
        <div
          className={classnames(
            styles['payment-options-row'],
            styles['payment-info-row-acima'],
            {
              [styles.selected]: selectedPaymentMethod === paymentMethods.ACIMA,
              [styles['not-selected']]:
                selectedPaymentMethod !== paymentMethods.ACIMA,
              [styles.hidden]: isStripeRegularCheckoutEnabled,
            },
          )}
          onClick={() => {
            onChangeSelectedPaymentMethod(paymentMethods.ACIMA);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onChangeSelectedPaymentMethod(paymentMethods.ACIMA);
            }
          }}
        >
          <div className={styles['payment-info-row-title']}>
            <div className={styles['payment-info-row-button-and-legend-acima']}>
              <div className={styles['payment-info-row-button']}>
                <RadioButton
                  name="paymentMethodOption"
                  id={`payment-${paymentMethods.ACIMA}`}
                  checked={selectedPaymentMethod === paymentMethods.ACIMA}
                  value={paymentMethods.ACIMA}
                  onChange={() => {
                    onChangeSelectedPaymentMethod(paymentMethods.ACIMA);
                  }}
                  disabled={containsNonLeasableItems}
                  theme="unselected-circle"
                  dataTestId="acima-radioButton"
                  label={
                    <div
                      className={classnames(
                        styles['payment-info-row-logo'],
                        styles['payment-info-row-logo-acima'],
                      )}
                    >
                      <img
                        alt={formatMessage(messages.acimaLogoAlt)}
                        src={acimaLogo as unknown as string}
                        className={styles['acima-img']}
                      />
                    </div>
                  }
                />
              </div>
              <div className={styles['acima-info']}>
                {formatMessage(messages.acimaLegend)}
              </div>
              {showAcimaPaymentMethod &&
                (selectedPaymentMethod === paymentMethods.ACIMA ||
                  isStripeRegularCheckoutEnabled) && (
                  <div className={styles['acima-button-container']}>
                    <AcimaButton
                      onSuccess={handleSavePaymentForm}
                      isLoading={false}
                      onModalError={onChangeSelectedPaymentMethod}
                      ref={acimaButtonRef}
                    />
                  </div>
                )}
            </div>
          </div>
          <AffirmForm />
        </div>
      )}
      {showTrueMedPaymentMethod && (
        <div
          className={classnames(styles['payment-options-row'], {
            [styles['payment-info-row-paypal']]: showChargeAfterPaymentMethod,
            [styles.selected]: selectedPaymentMethod === paymentMethods.TRUEMED,
            [styles['not-selected']]:
              selectedPaymentMethod !== paymentMethods.TRUEMED,
            [styles.hidden]: isStripeRegularCheckoutEnabled,
          })}
          onClick={() => {
            onChangeSelectedPaymentMethod(paymentMethods.TRUEMED);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onChangeSelectedPaymentMethod(paymentMethods.TRUEMED);
            }
          }}
        >
          <div className={styles['payment-info-row-title']}>
            <div className={styles['payment-info-row-button']}>
              <RadioButton
                name="paymentMethodOption"
                id={`payment-${paymentMethods.TRUEMED}`}
                checked={selectedPaymentMethod === paymentMethods.TRUEMED}
                value={paymentMethods.TRUEMED}
                onChange={() => {
                  onChangeSelectedPaymentMethod(paymentMethods.TRUEMED);
                }}
                theme="unselected-circle"
                dataTestId="truemed-radioButton"
                label={
                  <div
                    className={classnames(
                      styles['payment-info-row-logo'],
                      styles['payment-info-row-logo-truemed'],
                    )}
                  >
                    <span className={styles['payment-info-row-subtext']}>
                      {formatMessage(messages.truemedPaymentText)}
                    </span>
                    <img
                      alt={formatMessage(messages.truemedAltText)}
                      src={truemedIcon as unknown as string}
                    />
                  </div>
                }
              />
            </div>
          </div>
          {(isStripeRegularCheckoutEnabled ||
            selectedPaymentMethod === paymentMethods.TRUEMED) && (
            <div className={styles['truemed-button-container']}>
              <TruemedButton
                onSuccess={handleSavePaymentForm}
                ref={truemedButtonRef}
              />
            </div>
          )}
        </div>
      )}
      {!showAffirmPaymentMethod && !showChargeAfterPaymentMethod && (
        <div className={styles['payment-info-row-affirm-upper-separator']} />
      )}
      <div
        className={classnames(
          styles['payment-options-row'],
          styles['payment-info-row-charge-after'],
          {
            [styles.hidden]:
              !showChargeAfterPaymentMethod || isStripeRegularCheckoutEnabled,
            [styles.selected]:
              selectedPaymentMethod === paymentMethods.CHARGE_AFTER,
            [styles['not-selected']]:
              selectedPaymentMethod !== paymentMethods.CHARGE_AFTER,
          },
        )}
        onClick={() => {
          onChangeSelectedPaymentMethod(paymentMethods.CHARGE_AFTER);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onChangeSelectedPaymentMethod(paymentMethods.CHARGE_AFTER);
          }
        }}
      >
        <div
          className={classnames(
            styles['payment-info-row-title'],
            styles['payment-info-row-charge-after'],
          )}
        >
          <div
            className={
              styles['payment-info-row-button-and-legend-charge-after']
            }
          >
            <div className={styles['payment-info-row-button']}>
              <RadioButton
                name="paymentMethodOption"
                id={`payment-${paymentMethods.CHARGE_AFTER}`}
                checked={selectedPaymentMethod === paymentMethods.CHARGE_AFTER}
                value={paymentMethods.CHARGE_AFTER}
                onChange={() => {
                  onChangeSelectedPaymentMethod(paymentMethods.CHARGE_AFTER);
                }}
                disabled={containsNonLeasableItems}
                theme="unselected-circle"
                dataTestId="charge-after-radioButton"
                label={
                  <div className={styles['payment-info-row-logo-charge-after']}>
                    <div className={styles['charge-after-img']}>
                      <div className={styles.chargeAfterSvg} />
                    </div>
                  </div>
                }
              />
            </div>
          </div>
          <div className={styles['charge-after-checkout-widget-container']}>
            <ChargeAfterWidget
              tag={CHARGE_AFTER_WIDGET_TAGS.checkoutPayment}
              total={
                total?.centAmount
                  ? total.centAmount / 10 ** total.fractionDigits
                  : 0
              }
              id="charge-after-checkout-widget"
              eventListeners={[
                {
                  selector: '.superscript-1',
                  eventListener: {
                    event: 'click',
                    callback: () => {
                      scrollToElement('section-id-terms');
                    },
                  },
                },
                {
                  selector: '.superscript-2',
                  eventListener: {
                    event: 'click',
                    callback: () => {
                      scrollToElement('section-id-terms');
                    },
                  },
                },
                {
                  selector: '.superscript-3',
                  eventListener: {
                    event: 'click',
                    callback: () => {
                      scrollToElement('section-id-terms');
                    },
                  },
                },
                {
                  selector: '.superscript-4',
                  eventListener: {
                    event: 'click',
                    callback: () => {
                      scrollToElement('section-id-terms');
                    },
                  },
                },
              ]}
            />
          </div>
          {(isStripeRegularCheckoutEnabled ||
            selectedPaymentMethod === paymentMethods.CHARGE_AFTER) && (
            <div
              className={classnames(
                {
                  [styles['hidden-button']]:
                    isStripeRegularCheckoutEnabled ||
                    selectedPaymentMethod !== paymentMethods.CHARGE_AFTER,
                },
                styles['normal-checkout-button-container'],
              )}
            >
              {(isStripeRegularCheckoutEnabled ||
                selectedPaymentMethod === paymentMethods.CHARGE_AFTER) && (
                <ChargeAfterButton
                  onSuccess={handleSavePaymentForm}
                  onFailure={resetPaymentMethodToDefault}
                  isLoading={false}
                  ref={chargeAfterButtonRef}
                />
              )}
            </div>
          )}
        </div>
        <ChargeAfterSdk />
      </div>
    </div>
  );
};

export default PaymentForm;
